import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const addReactionBodySchema = z.object({
  emoji: z.string().min(1),
});

export const removeReactionBodySchema = z.object({
  emoji: z.string().min(1),
});

export const reactionsWriteSuccessSchema = z.object({
  ok: z.literal(true),
});

async function parseReactionsWriteSuccess(
  response: Response,
  fallbackMessage: string,
): Promise<z.infer<typeof reactionsWriteSuccessSchema>> {
  const payload =
    typeof response.json === "function"
      ? await response.json().catch(() => null)
      : null;
  const parsed = reactionsWriteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError(fallbackMessage, 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export function messageReactionsPath(messageId: string): string {
  return `/api/messages/${encodeURIComponent(messageId)}/reactions`;
}

export async function addReactionRequest({
  apiBaseUrl,
  accessToken,
  messageId,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  messageId: string;
  body: z.input<typeof addReactionBodySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof reactionsWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = addReactionBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${messageReactionsPath(messageId)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to add reaction");
  }

  return parseReactionsWriteSuccess(response, "Invalid add reaction response");
}

export async function removeReactionRequest({
  apiBaseUrl,
  accessToken,
  messageId,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  messageId: string;
  body: z.input<typeof removeReactionBodySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof reactionsWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = removeReactionBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${messageReactionsPath(messageId)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to remove reaction");
  }

  return parseReactionsWriteSuccess(
    response,
    "Invalid remove reaction response",
  );
}
